/**
 * @class Node
 *
 * @classdesc Represents a node in the navigation graph.
 *
 * Explanation of position and bearing properties:
 *
 * When images are uploaded they will have GPS information in the EXIF, this is what
 * is called `originalLatLon` {@link Node.originalLatLon}.
 *
 * When Structure from Motions has been run for a node a `computedLatLon` that
 * differs from the `originalLatLon` will be created. It is different because
 * GPS positions are not very exact and SfM aligns the camera positions according
 * to the 3D reconstruction {@link Node.computedLatLon}.
 *
 * At last there exist a `latLon` property which evaluates to
 * the `computedLatLon` from SfM if it exists but falls back
 * to the `originalLatLon` from the EXIF GPS otherwise {@link Node.latLon}.
 *
 * Everything that is done in in the Viewer is based on the SfM positions,
 * i.e. `computedLatLon`. That is why the smooth transitions go in the right
 * direction (nd not in strange directions because of bad GPS).
 *
 * E.g. when placing a marker in the Viewer it is relative to the SfM
 * position i.e. the `computedLatLon`.
 *
 * The same concept as above also applies to the compass angle (or bearing) properties
 * `originalCa`, `computedCa` and `ca`.
 */
export class Node {
    /**
     * Create a new node instance.
     *
     * @description Nodes are always created internally by the library.
     * Nodes can not be added to the library through any API method.
     *
     * @param {ICoreNode} coreNode - Raw core node data.
     * @ignore
     */
    constructor(core) {
        this._cache = null;
        this._core = core;
        this._fill = null;
    }
    /**
     * Get assets cached.
     *
     * @description The assets that need to be cached for this property
     * to report true are the following: fill properties, image and mesh.
     * The library ensures that the current node will always have the
     * assets cached.
     *
     * @returns {boolean} Value indicating whether all assets have been
     * cached.
     *
     * @ignore
     */
    get assetsCached() {
        return this._core != null && this._fill != null && this._cache != null;
    }
    get frame() {
        if (this._fill != null) {
            return this._fill.frame;
        }
        return null;
    }
    /**
     * Get alt.
     *
     * @description If SfM has not been run the computed altitude is
     * set to a default value of two meters.
     *
     * @returns {number} Altitude, in meters.
     */
    get alt() {
        return this._fill.calt;
    }
    /**
     * Get ca.
     *
     * @description If the SfM computed compass angle exists it will
     * be returned, otherwise the original EXIF compass angle.
     *
     * @returns {number} Compass angle, measured in degrees
     * clockwise with respect to north.
     */
    get ca() {
        return this._fill.cca != null ? this._fill.cca : this._fill.ca;
    }
    /**
     * Get cameraType.
     *
     * @description Will be undefined if SfM has not been run.
     *
     * @returns {CameraProjectionType} The camera type that captured the image.
     */
    get cameraType() {
        return this._fill.camera_projection_type;
    }
    /**
     * Get capturedAt.
     *
     * @returns {number} Timestamp when the image was captured.
     */
    get capturedAt() {
        return this._fill.captured_at;
    }
    /**
     * Get camera uuid.
     *
     * @description Will be undefined if the camera uuid was not
     * recorded in the image exif information.
     *
     * @returns {string} Universally unique id for camera used
     * when capturing image.
     */
    get cameraUuid() {
        return this._fill.captured_with_camera_uuid;
    }
    /**
     * Get clusterKey.
     *
     * @returns {string} Unique key of the SfM cluster to which
     * the node belongs.
     */
    get clusterKey() {
        return this._fill.cluster_key;
    }
    /**
     * Get ck1.
     *
     * @description Will not be set if SfM has not been run.
     *
     * @returns {number} SfM computed radial distortion parameter
     * k1.
     */
    get ck1() {
        return this._fill.ck1;
    }
    /**
     * Get ck2.
     *
     * @description Will not be set if SfM has not been run.
     *
     * @returns {number} SfM computed radial distortion parameter
     * k2.
     */
    get ck2() {
        return this._fill.ck2;
    }
    /**
     * Get computedCA.
     *
     * @description Will not be set if SfM has not been run.
     *
     * @returns {number} SfM computed compass angle, measured
     * in degrees clockwise with respect to north.
     */
    get computedCA() {
        return this._fill.cca;
    }
    /**
     * Get computedLatLon.
     *
     * @description Will not be set if SfM has not been run.
     *
     * @returns {ILatLon} SfM computed latitude longitude in WGS84 datum,
     * measured in degrees.
     */
    get computedLatLon() {
        return this._core.cl;
    }
    /**
     * Get focal.
     *
     * @description Will not be set if SfM has not been run.
     *
     * @returns {number} SfM computed focal length.
     */
    get focal() {
        return this._fill.cfocal;
    }
    /**
     * Get full.
     *
     * @description The library ensures that the current node will
     * always be full.
     *
     * @returns {boolean} Value indicating whether the node has all
     * properties filled.
     *
     * @ignore
     */
    get full() {
        return this._fill != null;
    }
    /**
     * Get height.
     *
     * @returns {number} Height of original image, not adjusted
     * for orientation.
     */
    get height() {
        return this._fill.height;
    }
    /**
     * Get image.
     *
     * @description The image will always be set on the current node.
     *
     * @returns {HTMLImageElement} Cached image element of the node.
     */
    get image() {
        return this._cache.image;
    }
    /**
     * Get image$.
     *
     * @returns {Observable<HTMLImageElement>} Observable emitting
     * the cached image when it is updated.
     *
     * @ignore
     */
    get image$() {
        return this._cache.image$;
    }
    /**
     * Get key.
     *
     * @returns {string} Unique key of the node.
     */
    get key() {
        return this._core.key;
    }
    /**
     * Get latLon.
     *
     * @description If the SfM computed latitude longitude exist
     * it will be returned, otherwise the original EXIF latitude
     * longitude.
     *
     * @returns {ILatLon} Latitude longitude in WGS84 datum,
     * measured in degrees.
     */
    get latLon() {
        return this._core.cl != null ? this._core.cl : this._core.l;
    }
    /**
     * Get merged.
     *
     * @returns {boolean} Value indicating whether SfM has been
     * run on the node and the node has been merged into a
     * connected component.
     */
    get merged() {
        return this._fill != null && this._fill.merge_version != null && this._fill.merge_version > 0;
    }
    /**
     * Get mergeCC.
     *
     * @description Will not be set if SfM has not yet been run on
     * node.
     *
     * @returns {number} SfM connected component key to which
     * image belongs.
     */
    get mergeCC() {
        return this._fill.merge_cc;
    }
    /**
     * Get mergeVersion.
     *
     * @returns {number} Version for which SfM was run and image was merged.
     */
    get mergeVersion() {
        return this._fill.merge_version;
    }
    /**
     * Get organizationKey.
     *
     * @returns {string} Unique key of the organization to which
     * the node belongs. If the node does not belong to an
     * organization the organization key will be undefined.
     */
    get organizationKey() {
        return this._fill.organization_key;
    }
    /**
     * Get orientation.
     *
     * @returns {number} EXIF orientation of original image.
     */
    get orientation() {
        return this._fill.orientation;
    }
    /**
     * Get originalAlt.
     *
     * @returns {number} EXIF altitude, in meters, if available.
     */
    get originalAlt() {
        return this._fill.altitude;
    }
    /**
     * Get originalCA.
     *
     * @returns {number} Original EXIF compass angle, measured in
     * degrees.
     */
    get originalCA() {
        return this._fill.ca;
    }
    /**
     * Get originalLatLon.
     *
     * @returns {ILatLon} Original EXIF latitude longitude in
     * WGS84 datum, measured in degrees.
     */
    get originalLatLon() {
        return this._core.l;
    }
    /**
     * Get private.
     *
     * @returns {boolean} Value specifying if image is accessible to
     * organization members only or to everyone.
     */
    get private() {
        return this._fill.private;
    }
    /**
     * Get projectKey.
     *
     * @returns {string} Unique key of the project to which
     * the node belongs. If the node does not belong to a
     * project the project key will be undefined.
     *
     * @ignore
     */
    get projectKey() {
        return this._fill.project != null ? this._fill.project.key : null;
    }
    /**
     * Get quality score.
     *
     * @returns {number} A number between zero and one
     * determining the quality of the image. Blurriness
     * (motion blur / out-of-focus), occlusion (camera
     * mount, ego vehicle, water-drops), windshield
     * reflections, bad illumination (exposure, glare),
     * and bad weather condition (fog, rain, snow)
     * affect the quality score.
     */
    get qualityScore() {
        return this._fill.quality_score;
    }
    /**
     * Get rotation.
     *
     * @description Will not be set if SfM has not been run.
     *
     * @returns {Array<number>} Rotation vector in angle axis representation.
     */
    get rotation() {
        return this._fill.c_rotation;
    }
    /**
     * Get scale.
     *
     * @description Will not be set if SfM has not been run.
     *
     * @returns {number} Scale of atomic reconstruction.
     */
    get scale() {
        return this._fill.atomic_scale;
    }
    /**
     * Get sequenceKey.
     *
     * @returns {string} Unique key of the sequence to which
     * the node belongs.
     */
    get sequenceKey() {
        return this._core.sequence_key;
    }
    /**
     * Get sequenceEdges.
     *
     * @returns {IEdgeStatus} Value describing the status of the
     * sequence edges.
     *
     * @ignore
     */
    get sequenceEdges() {
        return this._cache.sequenceEdges;
    }
    /**
     * Get sequenceEdges$.
     *
     * @description Internal observable, should not be used as an API.
     *
     * @returns {Observable<IEdgeStatus>} Observable emitting
     * values describing the status of the sequence edges.
     *
     * @ignore
     */
    get sequenceEdges$() {
        return this._cache.sequenceEdges$;
    }
    /**
     * Get spatialEdges.
     *
     * @returns {IEdgeStatus} Value describing the status of the
     * spatial edges.
     *
     * @ignore
     */
    get spatialEdges() {
        return this._cache.spatialEdges;
    }
    /**
     * Get spatialEdges$.
     *
     * @description Internal observable, should not be used as an API.
     *
     * @returns {Observable<IEdgeStatus>} Observable emitting
     * values describing the status of the spatial edges.
     *
     * @ignore
     */
    get spatialEdges$() {
        return this._cache.spatialEdges$;
    }
    /**
     * Get userKey.
     *
     * @returns {string} Unique key of the user who uploaded
     * the image.
     */
    get userKey() {
        return this._fill.user.key;
    }
    /**
     * Get username.
     *
     * @returns {string} Username of the user who uploaded
     * the image.
     */
    get username() {
        return this._fill.user.username;
    }
    /**
     * Get width.
     *
     * @returns {number} Width of original image, not
     * adjusted for orientation.
     */
    get width() {
        return this._fill.width;
    }
    /**
     * Cache the sequence edges.
     *
     * @description The sequence edges are cached asynchronously
     * internally by the library.
     *
     * @param {Array<IEdge>} edges - Sequence edges to cache.
     * @ignore
     */
    cacheSequenceEdges(edges) {
        this._cache.cacheSequenceEdges(edges);
    }
    /**
     * Cache the spatial edges.
     *
     * @description The spatial edges are cached asynchronously
     * internally by the library.
     *
     * @param {Array<IEdge>} edges - Spatial edges to cache.
     * @ignore
     */
    cacheSpatialEdges(edges) {
        this._cache.cacheSpatialEdges(edges);
    }
    /**
     * Dispose the node.
     *
     * @description Disposes all cached assets.
     * @ignore
     */
    dispose() {
        if (this._cache != null) {
            this._cache.dispose();
            this._cache = null;
        }
        this._core = null;
        this._fill = null;
    }
    /**
     * Initialize the node cache.
     *
     * @description The node cache is initialized internally by
     * the library.
     *
     * @param {NodeCache} cache - The node cache to set as cache.
     * @ignore
     */
    initializeCache(cache) {
        if (this._cache != null) {
            console.warn(`Node cache already initialized (${this.key}).`);
        }
        this._cache = cache;
    }
    /**
     * Fill the node with all properties.
     *
     * @description The node is filled internally by
     * the library.
     *
     * @param {IFillNode} fill - The fill node struct.
     * @ignore
     */
    makeFull(fill) {
        if (fill == null) {
            throw new Error("Fill can not be null.");
        }
        this._fill = fill;
    }
    /**
     * Reset the sequence edges.
     *
     * @ignore
     */
    resetSequenceEdges() {
        this._cache.resetSequenceEdges();
    }
    /**
     * Reset the spatial edges.
     *
     * @ignore
     */
    resetSpatialEdges() {
        this._cache.resetSpatialEdges();
    }
    /**
     * Clears the image and mesh assets, aborts
     * any outstanding requests and resets edges.
     *
     * @ignore
     */
    uncache() {
        if (this._cache == null) {
            return;
        }
        this._cache.dispose();
        this._cache = null;
    }
}
//# sourceMappingURL=Node.js.map